import { useState, useEffect, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, ChevronLeft, ChevronRight, Check, Calendar as CalIcon, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * IntegrationsSetupWizard — Phase 2 of the QuickBooks integration.
 *
 * Five steps:
 *   1. Map accounts (SION categories → QB chart of accounts)
 *   2. VAT codes (SION VAT rates → QB tax codes)
 *   3. Customer + supplier matching (SION client_types/suppliers → QB Customers/Vendors)
 *   4. Sync start date
 *   5. Test sync (Phase 3 placeholder)
 *
 * Saves a single config payload to PUT /api/integrations/quickbooks/config
 * + customer/supplier matches via PUT /api/integrations/quickbooks/matches.
 */
const STEPS = ["Accounts", "VAT", "Customers & Suppliers", "Sync Start Date", "Test Sync"];

const ACTION_OPTIONS = [
  { value: "match", label: "Match existing" },
  { value: "create", label: "Create on first sync" },
  { value: "skip", label: "Skip" },
];

export default function IntegrationsSetupWizard({ provider = "quickbooks", currentUserName = "", open, onClose, onComplete }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [categories, setCategories] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [qbAccounts, setQbAccounts] = useState([]);
  const [qbTaxCodes, setQbTaxCodes] = useState([]);
  const [qbCustomers, setQbCustomers] = useState([]);
  const [qbVendors, setQbVendors] = useState([]);
  const [sionEntities, setSionEntities] = useState({ client_types: [], suppliers: [], contractors: [] });
  const [companyCountry, setCompanyCountry] = useState("");

  // Wizard state
  const [accountMap, setAccountMap] = useState({});
  const [vatMap, setVatMap] = useState({});
  const [customerMatches, setCustomerMatches] = useState([]);
  const [supplierMatches, setSupplierMatches] = useState([]);
  const [syncStartMode, setSyncStartMode] = useState("today");
  const [syncStartDate, setSyncStartDate] = useState(new Date().toISOString().slice(0, 10));

  // ── Initial load ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setLoading(true);
    Promise.all([
      api.get("/integrations/categories").then(r => r.data),
      api.get(`/integrations/${provider}/accounts`).then(r => r.data).catch(() => ({ accounts: [] })),
      api.get(`/integrations/${provider}/tax-codes`).then(r => r.data).catch(() => ({ tax_codes: [] })),
      api.get(`/integrations/${provider}/customers`).then(r => r.data).catch(() => ({ customers: [] })),
      api.get(`/integrations/${provider}/vendors`).then(r => r.data).catch(() => ({ vendors: [] })),
      api.get("/integrations/sion-entities").then(r => r.data),
      api.get(`/integrations/${provider}/config`).then(r => r.data).catch(() => ({})),
      api.get(`/integrations/${provider}/matches`).then(r => r.data).catch(() => ({ customers: [], suppliers: [] })),
    ]).then(([cats, acc, tax, cust, vend, ents, cfg, matches]) => {
      setCategories(cats.categories || []);
      setVatRates(cats.vat_rates || []);
      setQbAccounts(acc.accounts || []);
      setQbTaxCodes(tax.tax_codes || []);
      setQbCustomers(cust.customers || []);
      setQbVendors(vend.vendors || []);
      setSionEntities(ents);
      setCompanyCountry(cfg.company_country || "");
      setAccountMap(cfg.account_mapping || {});
      setVatMap(cfg.vat_mapping || {});
      // Seed matches: from saved if present, else generate placeholder rows from SION entities
      if ((matches.customers || []).length) {
        setCustomerMatches(matches.customers);
      } else {
        setCustomerMatches((ents.client_types || []).map(ct => ({ sion_key: ct, sion_label: ct, action: "create", qbo_id: "", qbo_name: "" })));
      }
      if ((matches.suppliers || []).length) {
        setSupplierMatches(matches.suppliers);
      } else {
        const sups = (ents.suppliers || []).map(s => ({ sion_key: s, sion_label: s, is_contractor: false, action: "create", qbo_id: "", qbo_name: "" }));
        const cons = (ents.contractors || []).map(c => ({ sion_key: c.id, sion_label: c.name, is_contractor: true, action: "create", qbo_id: "", qbo_name: "" }));
        setSupplierMatches([...cons, ...sups]);
      }
      if (cfg.sync_start_date) {
        setSyncStartDate(cfg.sync_start_date);
        setSyncStartMode("specific");
      }
    }).catch(e => {
      toast.error("Failed to load setup data: " + (e?.response?.data?.detail || e.message));
    }).finally(() => setLoading(false));
  }, [open, provider]);

  // ── Step navigation + validation ──────────────────────────────────────
  const canAdvance = useCallback(() => {
    if (step === 0) {
      // At least invoice_revenue + materials_cost should be picked
      return !!(accountMap.invoice_revenue && accountMap.materials_cost);
    }
    if (step === 1) {
      // If QB returned UK VAT codes (>2 typical), require at least standard_20 mapped.
      // For US sandboxes with only sales-tax codes we let the user skip.
      if (qbTaxCodes.length >= 3) return !!vatMap.standard_20;
      return true;
    }
    if (step === 2) {
      // Every row must have an action; 'match' rows must have qbo_id
      const ok = (rows) => rows.every(r => r.action === "skip" || r.action === "create" || (r.action === "match" && r.qbo_id));
      return ok(customerMatches) && ok(supplierMatches);
    }
    if (step === 3) {
      return !!syncStartDate;
    }
    return true;
  }, [step, accountMap, vatMap, qbTaxCodes, customerMatches, supplierMatches, syncStartDate]);

  // ── Final save (called from step 5) ───────────────────────────────────
  const finalSave = async () => {
    setSaving(true);
    try {
      await api.put(`/integrations/${provider}/config`, {
        account_mapping: accountMap,
        vat_mapping: vatMap,
        sync_start_date: syncStartDate,
        setup_complete: true,
        saved_by: currentUserName,
      });
      await api.put(`/integrations/${provider}/matches`, {
        customers: customerMatches,
        suppliers: supplierMatches,
        saved_by: currentUserName,
      });
      toast.success("Setup complete — SION is ready to sync to QuickBooks");
      onComplete && onComplete();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save setup");
    } finally {
      setSaving(false);
    }
  };

  // ── Step bodies ───────────────────────────────────────────────────────
  const incomeAccounts = useMemo(() => qbAccounts.filter(a => (a.classification || "").toLowerCase() === "revenue" || (a.account_type || "").toLowerCase().includes("income")), [qbAccounts]);
  const expenseAccounts = useMemo(() => qbAccounts.filter(a => (a.classification || "").toLowerCase() === "expense" || (a.account_type || "").toLowerCase().includes("expense") || (a.account_type || "").toLowerCase().includes("cost")), [qbAccounts]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="integrations-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" style={{ color: "var(--primary)" }} />
            QuickBooks Setup — Step {step + 1} of {STEPS.length}: {STEPS[step]}
          </DialogTitle>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex items-center gap-1 px-1 pb-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-1">
              <div
                className="flex-1 h-1.5 rounded-full transition-colors"
                style={{ background: i <= step ? "var(--primary)" : "var(--border)" }}
              />
              {i < STEPS.length - 1 && <div className="w-1" />}
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span className="text-sm" style={{ color: "var(--text-muted)" }}>Loading data from QuickBooks…</span>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto pr-2" data-testid={`wizard-step-${step}`}>
            {step === 0 && (
              <StepAccounts
                categories={categories}
                qbAccounts={qbAccounts}
                incomeAccounts={incomeAccounts}
                expenseAccounts={expenseAccounts}
                value={accountMap}
                onChange={setAccountMap}
              />
            )}
            {step === 1 && (
              <StepVat
                vatRates={vatRates}
                qbTaxCodes={qbTaxCodes}
                value={vatMap}
                onChange={setVatMap}
                companyCountry={companyCountry}
              />
            )}
            {step === 2 && (
              <StepMatches
                qbCustomers={qbCustomers}
                qbVendors={qbVendors}
                customerMatches={customerMatches}
                setCustomerMatches={setCustomerMatches}
                supplierMatches={supplierMatches}
                setSupplierMatches={setSupplierMatches}
              />
            )}
            {step === 3 && (
              <StepSyncStart
                mode={syncStartMode}
                setMode={setSyncStartMode}
                date={syncStartDate}
                setDate={setSyncStartDate}
              />
            )}
            {step === 4 && (
              <StepTestSync
                accountMap={accountMap}
                vatMap={vatMap}
                customerMatches={customerMatches}
                supplierMatches={supplierMatches}
                syncStartDate={syncStartDate}
              />
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: "var(--border)" }}>
          <Button
            variant="outline"
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0 || saving}
            data-testid="wizard-back"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            {step < 4 ? `Step ${step + 1} of ${STEPS.length}` : "Final step — review and enable sync"}
          </span>
          {step < STEPS.length - 1 ? (
            <Button
              onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
              disabled={!canAdvance() || saving}
              data-testid="wizard-next"
              style={{ background: "var(--primary)", color: "#fff" }}
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={finalSave}
              disabled={saving}
              data-testid="wizard-finish"
              style={{ background: "#2DA01C", color: "#fff" }}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              {saving ? "Saving…" : "Enable Sync"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── STEP 1 — Account Mapping ────────────────────────────────────────────
function StepAccounts({ categories, qbAccounts, incomeAccounts, expenseAccounts, value, onChange }) {
  return (
    <div className="space-y-3">
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        Pick which QuickBooks chart-of-account each SION category posts to. These mappings are reused for every sync.
      </p>
      <div className="grid grid-cols-1 gap-2.5">
        {categories.map(cat => {
          const choices = cat.side === "income" ? incomeAccounts : expenseAccounts;
          const pool = choices.length ? choices : qbAccounts;
          return (
            <div key={cat.key} className="grid grid-cols-12 items-center gap-3 p-3 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
              <div className="col-span-5">
                <div className="text-sm font-semibold">{cat.label}</div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{cat.hint}</div>
              </div>
              <div className="col-span-1 flex justify-center text-slate-400">→</div>
              <div className="col-span-6">
                <Select
                  value={value[cat.key] || ""}
                  onValueChange={(v) => onChange({ ...value, [cat.key]: v })}
                >
                  <SelectTrigger data-testid={`acct-map-${cat.key}`}><SelectValue placeholder="Select a QuickBooks account…" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {pool.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name} <span className="text-xs" style={{ color: "var(--text-muted)" }}>({a.account_type})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── STEP 2 — VAT Codes ──────────────────────────────────────────────────
function StepVat({ vatRates, qbTaxCodes, value, onChange, companyCountry }) {
  const isUS = (companyCountry || "").toUpperCase() === "US";
  return (
    <div className="space-y-3">
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        Map SION's VAT rates to your QuickBooks tax codes so VAT totals reconcile correctly at return time.
      </p>
      {isUS && (
        <div className="p-3 rounded-lg flex items-start gap-2" style={{ background: "#FEF3C7", border: "1px solid #F59E0B" }}>
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#92400E" }} />
          <p className="text-xs" style={{ color: "#78350F" }}>
            Your QuickBooks company is registered in the US and uses Sales Tax, not VAT. This step is optional in sandbox — leave it blank and SION will not post VAT codes. When you connect a UK production company in Phase 1.5, this step becomes required.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 gap-2.5">
        {vatRates.map(vr => (
          <div key={vr.key} className="grid grid-cols-12 items-center gap-3 p-3 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
            <div className="col-span-5 text-sm font-semibold">{vr.label}</div>
            <div className="col-span-1 flex justify-center text-slate-400">→</div>
            <div className="col-span-6">
              <Select value={value[vr.key] || "__none__"} onValueChange={(v) => onChange({ ...value, [vr.key]: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid={`vat-map-${vr.key}`}><SelectValue placeholder="Select a QB tax code…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__"><span style={{ color: "var(--text-muted)" }}>— No mapping —</span></SelectItem>
                  {qbTaxCodes.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} {t.description && <span className="text-xs" style={{ color: "var(--text-muted)" }}>({t.description})</span>}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── STEP 3 — Customer + Supplier matching ──────────────────────────────
function StepMatches({ qbCustomers, qbVendors, customerMatches, setCustomerMatches, supplierMatches, setSupplierMatches }) {
  const updateRow = (rows, setRows, sionKey, patch) => setRows(rows.map(r => (r.sion_key === sionKey ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-4">
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        For each SION client type and supplier/contractor, choose whether to match to an existing QuickBooks record, create a new one on first sync, or skip entirely.
      </p>

      <div className="ui-surface p-3">
        <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "var(--text-muted)" }}>Customers ({customerMatches.length})</div>
        <MatchTable rows={customerMatches} pool={qbCustomers} onChange={(k, p) => updateRow(customerMatches, setCustomerMatches, k, p)} testIdPrefix="cust" />
      </div>

      <div className="ui-surface p-3">
        <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "var(--text-muted)" }}>Suppliers &amp; Contractors ({supplierMatches.length})</div>
        <MatchTable rows={supplierMatches} pool={qbVendors} onChange={(k, p) => updateRow(supplierMatches, setSupplierMatches, k, p)} testIdPrefix="sup" />
      </div>
    </div>
  );
}

function MatchTable({ rows, pool, onChange, testIdPrefix }) {
  if (!rows.length) return <p className="text-sm py-3" style={{ color: "var(--text-muted)" }}>None.</p>;
  return (
    <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
      {rows.map((r) => (
        <div key={`${testIdPrefix}-${r.sion_key}`} className="grid grid-cols-12 items-center gap-2 py-1.5 px-2 rounded" style={{ background: "var(--bg)" }}>
          <div className="col-span-4 text-sm font-medium truncate flex items-center gap-1">
            {r.sion_label}
            {r.is_contractor && <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: "rgba(56,189,248,0.15)", color: "#0369A1" }}>Contractor</span>}
          </div>
          <div className="col-span-3">
            <Select value={r.action} onValueChange={(v) => onChange(r.sion_key, { action: v, qbo_id: v === "match" ? r.qbo_id : "", qbo_name: v === "match" ? r.qbo_name : "" })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`${testIdPrefix}-action-${r.sion_key}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-5">
            {r.action === "match" && (
              <Select value={r.qbo_id || ""} onValueChange={(v) => {
                const item = pool.find(p => p.id === v);
                onChange(r.sion_key, { qbo_id: v, qbo_name: item?.display_name || "" });
              }}>
                <SelectTrigger className="h-8 text-xs" data-testid={`${testIdPrefix}-target-${r.sion_key}`}><SelectValue placeholder="Pick QuickBooks record…" /></SelectTrigger>
                <SelectContent className="max-h-[260px]">
                  {pool.map(p => <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── STEP 4 — Sync Start Date ────────────────────────────────────────────
function StepSyncStart({ mode, setMode, date, setDate }) {
  const today = new Date().toISOString().slice(0, 10);
  const fyStart = (() => {
    // UK FY: 6 April (HMRC personal tax) — using April 6 of the current year for simplicity
    const yr = new Date().getMonth() < 3 ? new Date().getFullYear() - 1 : new Date().getFullYear();
    return `${yr}-04-06`;
  })();
  const apply = (m, d) => { setMode(m); setDate(d); };
  return (
    <div className="space-y-3">
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        Choose the cutoff. Anything before this date is excluded from automated sync — prevents flooding QuickBooks with history you may have already entered manually.
      </p>
      <div className="space-y-2">
        <RadioCard active={mode === "today"} onClick={() => apply("today", today)} title="From today only" subtitle={`Cutoff: ${today}`} testId="sync-mode-today" />
        <RadioCard active={mode === "fy"} onClick={() => apply("fy", fyStart)} title="From start of current financial year" subtitle={`Cutoff: ${fyStart} (UK personal FY starts 6 Apr)`} testId="sync-mode-fy" />
        <div className="rounded-lg p-3" style={{ border: `2px solid ${mode === "specific" ? "var(--primary)" : "var(--border)"}` }}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 cursor-pointer" onClick={() => apply("specific", date)}>
              <div className="text-sm font-semibold">From a specific date</div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>Pick any date — only records on/after this date sync.</div>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="sync-mode-specific">
                  <CalIcon className="w-3.5 h-3.5 mr-1" />{date}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0">
                <Calendar mode="single" selected={new Date(date)} onSelect={(d) => d && apply("specific", d.toISOString().slice(0, 10))} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </div>
  );
}

function RadioCard({ active, onClick, title, subtitle, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full text-left rounded-lg p-3 transition-colors"
      style={{
        border: `2px solid ${active ? "var(--primary)" : "var(--border)"}`,
        background: active ? "rgba(59, 130, 246, 0.05)" : "transparent",
      }}
    >
      <div className="text-sm font-semibold">{title}</div>
      <div className="text-xs" style={{ color: "var(--text-muted)" }}>{subtitle}</div>
    </button>
  );
}

// ── STEP 5 — Test Sync (Phase 3 placeholder, summary view) ─────────────
function StepTestSync({ accountMap, vatMap, customerMatches, supplierMatches, syncStartDate }) {
  const accountCount = Object.values(accountMap).filter(Boolean).length;
  const vatCount = Object.values(vatMap).filter(Boolean).length;
  const matchedCust = customerMatches.filter(r => r.action === "match").length;
  const createCust = customerMatches.filter(r => r.action === "create").length;
  const matchedSup = supplierMatches.filter(r => r.action === "match").length;
  const createSup = supplierMatches.filter(r => r.action === "create").length;

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-lg" style={{ background: "rgba(45,160,28,0.08)", border: "1px solid #2DA01C" }}>
        <p className="text-sm font-semibold" style={{ color: "#15803D" }}>Setup summary</p>
        <p className="text-xs mt-1" style={{ color: "#166534" }}>Review below and click <strong>Enable Sync</strong> to save. SION will start posting to QuickBooks for new transactions from <strong>{syncStartDate}</strong> onwards.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SummaryStat label="Account mappings" value={`${accountCount}`} />
        <SummaryStat label="VAT code mappings" value={`${vatCount}`} />
        <SummaryStat label="Customers matched" value={`${matchedCust}`} sub={`${createCust} will be created`} />
        <SummaryStat label="Suppliers matched" value={`${matchedSup}`} sub={`${createSup} will be created`} />
        <SummaryStat label="Sync start date" value={syncStartDate} />
        <SummaryStat label="Test sync" value="Phase 3" sub="Real test send unlocks once invoice push is built" />
      </div>
      <div className="p-3 rounded-lg text-xs" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-muted)" }}>
        <p className="font-semibold mb-1" style={{ color: "var(--text-secondary)" }}>What happens after you click Enable Sync</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>Your mapping config is saved permanently.</li>
          <li>New invoices, payments, expenses and contractor pay records dated on/after <strong>{syncStartDate}</strong> will queue for sync (Phase 3 enables actually sending).</li>
          <li>You can re-run this wizard any time from the Integrations card.</li>
        </ul>
      </div>
    </div>
  );
}

function SummaryStat({ label, value, sub }) {
  return (
    <div className="p-3 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
      {sub && <div className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>{sub}</div>}
    </div>
  );
}

// Suppress unused-imports warnings (kept for future use in Phase 3 expansion)
void Input; void Label;
